import type { ReactNode } from 'react';
import { FaTimes } from 'react-icons/fa';
import type { AiRecommendation } from '../types';
import { formatConfidence, formatPdefault, getPdefaultColorClass, getRecommendationConfig } from '../utils/aiAnalysisUtils';

/**
 * Shared presentation components for AI analysis results, reused by
 * AiAnalysisModal (per-batch results list) and AiRationaleModal (single drawee).
 *
 * These are intentionally "dumb" components driven by primitive props so both
 * the AiAnalysisResultItem and CreditAnalysis shapes can feed them.
 */

// ═══════════════════════════════════════════════════════════════
// INTERFACES & TYPES
// ═══════════════════════════════════════════════════════════════

interface AiResultData {
    recommendation: AiRecommendation | null | undefined;
    confidence: number | null | undefined;
    pdefault1y: number | null | undefined;
    rationale: string | null | undefined;
    riskFactors: string[];
    positiveFactors: string[];
    errorMessage: string | null | undefined;
}

// ═══════════════════════════════════════════════════════════════
// COMPONENTS
// ═══════════════════════════════════════════════════════════════

/** The "X" icon button shown in a modal header. */
export function CloseButton({ onClose }: { onClose: () => void }) {
    return (
        <button
            type="button"
            aria-label="Fechar"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors shrink-0"
        >
            <FaTimes className="w-5 h-5" />
        </button>
    );
}

/** Recommendation badge (icon + label). Renders nothing when no recommendation is present. */
export function RecommendationBadge({ recommendation, size = 'sm' }: { recommendation: AiRecommendation | null | undefined; size?: 'sm' | 'md' }) {
    if (!recommendation) return null;
    const config = getRecommendationConfig(recommendation);
    const Icon = config.icon;
    const sizeClass = size === 'md' ? 'gap-1.5 px-3 py-1.5 text-sm' : 'gap-1 px-2 py-1 text-xs';
    const iconClass = size === 'md' ? 'w-4 h-4' : 'w-3 h-3';
    return (
        <span className={`inline-flex items-center rounded font-medium ${sizeClass} ${config.badgeClass}`}>
            <Icon className={iconClass} />
            {config.label}
        </span>
    );
}

/** A single labelled metric (small label above a value). */
function Metric({ label, value, valueClass }: { label: string; value: ReactNode; valueClass: string }) {
    return (
        <div>
            <span className="block text-xs text-gray-500 dark:text-gray-400">{label}</span>
            <span className={`text-lg font-semibold ${valueClass}`}>{value}</span>
        </div>
    );
}

/** PDefault + Confidence metrics side by side. */
export function AiMetrics({ pdefault1y, confidence }: { pdefault1y: number | null | undefined; confidence: number | null | undefined }) {
    return (
        <div className="flex gap-6">
            <Metric label="Prob. Default 1 ano (IA)" value={formatPdefault(pdefault1y)} valueClass={getPdefaultColorClass(pdefault1y)} />
            <Metric label="Confiança" value={formatConfidence(confidence)} valueClass="text-gray-700 dark:text-gray-300" />
        </div>
    );
}

/** The rationale text block. */
export function AiRationale({ rationale }: { rationale: string | null | undefined }) {
    return (
        <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Racional</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">
                {rationale || 'Sem racional disponível.'}
            </p>
        </div>
    );
}

/** A titled bullet list of factors (risk or positive). Renders nothing when empty. */
export function AiFactorList({ title, tone, items }: { title: string; tone: 'risk' | 'positive'; items: string[] }) {
    if (items.length === 0) return null;
    const titleClass = tone === 'risk' ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400';
    return (
        <div className="mt-4">
            <h4 className={`text-sm font-medium mb-1 ${titleClass}`}>{title}</h4>
            <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-0.5">
                {items.map((f, i) => <li key={`${i}-${f}`}>{f}</li>)}
            </ul>
        </div>
    );
}

/** Inline error banner for a failed AI analysis. */
export function AiErrorBanner({ message }: { message: string }) {
    return (
        <div className="p-3 bg-red-50 dark:bg-red-900/30 rounded text-sm text-red-700 dark:text-red-300">
            <strong>Erro na análise:</strong> {message}
        </div>
    );
}

/**
 * The full result body: metrics + rationale + factors, or an error banner.
 * Optionally prefixed by a leading node (e.g. a recommendation badge) and
 * suffixed by a footer node (e.g. processing time).
 */
export function AiResultBody({ data, leading, footer }: { data: AiResultData; leading?: ReactNode; footer?: ReactNode }) {
    if (data.errorMessage) {
        return <AiErrorBanner message={data.errorMessage} />;
    }
    return (
        <>
            <div className="flex items-center gap-6">
                {leading}
                <AiMetrics pdefault1y={data.pdefault1y} confidence={data.confidence} />
            </div>
            <div className="mt-3">
                <AiRationale rationale={data.rationale} />
            </div>
            <AiFactorList title="Fatores de Risco" tone="risk" items={data.riskFactors} />
            <AiFactorList title="Fatores Positivos" tone="positive" items={data.positiveFactors} />
            {footer}
        </>
    );
}
